import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import InactivityManager from "@/components/InactivityManager";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { isBriqueziAnota, isBriqueziNet } from "@/const";
import { trpc } from "@/lib/trpc";
import { Loader2 } from "lucide-react";
import React, { Suspense, lazy, useEffect } from "react";
import { Route, Switch, useLocation } from "wouter";

// Componente de carregamento para Lazy Loading de rotas
function PageLoader() {
  return (
    <div className="flex min-h-[40vh] w-full items-center justify-center p-8">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="text-xs text-muted-foreground font-medium">Carregando módulo...</span>
      </div>
    </div>
  );
}

// Lazy Loading de Páginas Públicas e Institucionais
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const RecuperarSenhaPage = lazy(() => import("./pages/RecuperarSenhaPage"));
const QuemSomos = lazy(() => import("./pages/QuemSomos"));
const OQueOferecemos = lazy(() => import("./pages/OQueOferecemos"));
const ServicoDetalhe = lazy(() => import("./pages/ServicoDetalhe"));
const Contato = lazy(() => import("./pages/Contato"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BriqueziLandingPage = lazy(() => import("@/pages/BriqueziLandingPage"));
const TermosDeUsoPage = lazy(() => import("@/pages/TermosDeUsoPage"));
const PortalTomadorPage = lazy(() => import("@/pages/PortalTomadorPage"));
const PoliticaPrivacidadePage = lazy(() => import("@/pages/PoliticaPrivacidadePage"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Lazy Loading de Páginas do Portal do Cliente
const PortalDashboard = lazy(() => import("@/pages/portal/PortalDashboard"));
const PendingsPage = lazy(() => import("@/pages/portal/PendingsPage"));
const DueDatesPage = lazy(() => import("@/pages/portal/DueDatesPage"));
const EmitirNotaPage = lazy(() => import("@/pages/portal/EmitirNotaPage"));
const TodasNotasPage = lazy(() => import("@/pages/portal/TodasNotasPage"));
const SectorPage = lazy(() => import("@/pages/portal/SectorPage"));

// Lazy Loading de Páginas do Painel Administrativo
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const CompaniesPage = lazy(() => import("@/pages/admin/CompaniesPage"));
const UsersPage = lazy(() => import("@/pages/admin/UsersPage"));
const AdminSectorsPage = lazy(() => import("@/pages/admin/AdminSectorsPage"));
const DocumentsPage = lazy(() => import("@/pages/admin/DocumentsPage"));
const AdminPendingsPage = lazy(() => import("@/pages/admin/AdminPendingsPage"));
const AdminDueDatesPage = lazy(() => import("@/pages/admin/AdminDueDatesPage"));
const AdminBlogPosts = lazy(() => import("./pages/admin/AdminBlogPosts"));
const AdminBlogPostEdit = lazy(() => import("./pages/admin/AdminBlogPostEdit"));
const AdminContactMessages = lazy(() => import("./pages/admin/AdminContactMessages"));
const AdminBlogCategories = lazy(() => import("./pages/admin/AdminBlogCategories"));
const CustomersPage = lazy(() => import("./pages/admin/CustomersPage"));
const ServicesPage = lazy(() => import("./pages/admin/ServicesPage"));
const FocusLogsPage = lazy(() => import("./pages/admin/FocusLogsPage"));
const AdminEmailLogsPage = lazy(() => import("./pages/admin/AdminEmailLogsPage"));
const AdminSettingsPage = lazy(() => import("./pages/admin/AdminSettingsPage"));
const AdminPlansPage = lazy(() => import("./pages/admin/AdminPlansPage"));
const AnalyticsDashboardPage = lazy(() => import("@/pages/admin/AnalyticsDashboardPage"));
const AdminGuiasPage = lazy(() => import("@/pages/admin/AdminGuiasPage"));

function PortalShell({ children }: { children: React.ReactNode }) {
  return <DashboardLayout>{children}</DashboardLayout>;
}

function RequireRole({
  role,
  children,
}: {
  role: "user" | "admin";
  children: React.ReactNode;
}) {
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    const isAdminOrManager = user.role === "admin" || user.role === "admin-interno" || user.role === "manager";
    if (role === "admin" && !isAdminOrManager) setLocation("/portal");
    if (role === "user" && isAdminOrManager) setLocation("/admin");
  }, [user, loading, role, setLocation]);

  return <>{children}</>;
}

function PortalRoutes() {
  const sectorsQuery = trpc.sectors.list.useQuery();
  const sectorList = sectorsQuery.data || [];

  return (
    <PortalShell>
      <RequireRole role="user">
        <Suspense fallback={<PageLoader />}>
          <Switch>
            <Route path="/portal" component={PortalDashboard} />
            <Route path="/portal/pendencias" component={PendingsPage} />
            <Route path="/portal/vencimentos" component={DueDatesPage} />
            <Route path="/portal/notas/emitir" component={EmitirNotaPage} />
            <Route path="/portal/emitir-nota" component={EmitirNotaPage} />
            <Route path="/portal/notas" component={TodasNotasPage} />
            <Route path="/portal/todas-as-notas" component={TodasNotasPage} />
            <Route path="/portal/analitico" component={AnalyticsDashboardPage} />
            <Route path="/portal/guias" component={AdminGuiasPage} />
            <Route path="/portal/clientes" component={CustomersPage} />
            <Route path="/portal/servicos" component={ServicesPage} />
            {sectorList.map((s) => (
              <Route
                key={s.key}
                path={s.path || `/portal/${s.key}`}
                component={() => <SectorPage sectorKey={s.key} />}
              />
            ))}
            {!sectorsQuery.isLoading && <Route component={NotFound} />}
          </Switch>
        </Suspense>
      </RequireRole>
    </PortalShell>
  );
}

function AdminRoutes() {
  const { user } = useAuth();
  const isAdminInterno = user?.role === "admin-interno";
  const isGroupLeader = (user as any)?.isGroupLeader === 1 || (user as any)?.isGroupLeader === true || (user as any)?.isGroupLeader === '1';
  const canSeePlans = user?.role === "admin" || (isAdminInterno && isGroupLeader);

  return (
    <PortalShell>
      <RequireRole role="admin">
        <Suspense fallback={<PageLoader />}>
          <Switch>
            <Route path="/admin" component={AdminDashboard} />
            <Route path="/admin/empresas" component={CompaniesPage} />
            <Route path="/admin/usuarios" component={UsersPage} />
            <Route path="/admin/setores" component={AdminSectorsPage} />
            <Route path="/admin/documentos" component={DocumentsPage} />
            <Route path="/admin/pendencias" component={AdminPendingsPage} />
            <Route path="/admin/vencimentos" component={AdminDueDatesPage} />
            <Route path="/admin/notas/emitir" component={EmitirNotaPage} />
            <Route path="/admin/emitir-nota" component={EmitirNotaPage} />
            <Route path="/admin/notas" component={TodasNotasPage} />
            <Route path="/admin/todas-as-notas" component={TodasNotasPage} />
            <Route path="/admin/analitico" component={AnalyticsDashboardPage} />
            <Route path="/admin/guias" component={AdminGuiasPage} />
            {!isAdminInterno && <Route path="/admin/blog" component={AdminBlogPosts} />}
            {!isAdminInterno && <Route path="/admin/blog/categorias" component={AdminBlogCategories} />}
            {!isAdminInterno && <Route path="/admin/blog/novo" component={AdminBlogPostEdit} />}
            {!isAdminInterno && <Route path="/admin/blog/editar/:id" component={AdminBlogPostEdit} />}
            {!isAdminInterno && <Route path="/admin/contato" component={AdminContactMessages} />}
            {canSeePlans && <Route path="/admin/planos" component={AdminPlansPage} />}
            <Route path="/admin/clientes" component={CustomersPage} />
            <Route path="/admin/servicos" component={ServicesPage} />
            <Route path="/admin/focus/logs" component={FocusLogsPage} />
            <Route path="/admin/emails" component={AdminEmailLogsPage} />
            <Route path="/admin/configuracoes" component={AdminSettingsPage} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </RequireRole>
    </PortalShell>
  );
}

function TenantRoute({ component: Component }: { component: React.ComponentType }) {
  const currentHost = typeof window !== "undefined" ? window.location.hostname : "";
  const { data: tenantBranding, isLoading } = trpc.companies.byDomain.useQuery(
    { domain: currentHost },
    { enabled: Boolean(currentHost) && !isBriqueziNet && !isBriqueziAnota, staleTime: 300000 }
  );
  const { branding, setCompanyBranding } = useTheme();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (tenantBranding) {
      setCompanyBranding(tenantBranding);
    }
  }, [tenantBranding, setCompanyBranding]);

  const effectiveBranding = tenantBranding ?? branding;
  const hasLanding = effectiveBranding?.hasLandingPage as any;
  const isLoginOnly = hasLanding === false || hasLanding === 0 || hasLanding === "0";

  useEffect(() => {
    if (isLoginOnly) {
      setLocation("/login");
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.replace("/login");
      }
    }
  }, [isLoginOnly, setLocation]);

  if (isLoginOnly) {
    return null;
  }

  if (isLoading && !effectiveBranding) {
    return <PageLoader />;
  }

  return <Component />;
}

function Router() {
  if (isBriqueziAnota) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/" component={() => {
            const [, setLocation] = useLocation();
            useEffect(() => { setLocation("/login"); }, [setLocation]);
            return null;
          }} />
          <Route path="/login" component={Login} />
          <Route path="/recuperar-senha" component={RecuperarSenhaPage} />
          <Route path="/esqueci-senha" component={RecuperarSenhaPage} />
          <Route path="/reset-password" component={RecuperarSenhaPage} />
          <Route path="/termos-de-uso" component={TermosDeUsoPage} />
          <Route path="/termos" component={TermosDeUsoPage} />
          <Route path="/politica-de-privacidade" component={PoliticaPrivacidadePage} />
          <Route path="/privacidade" component={PoliticaPrivacidadePage} />
          <Route path="/portal-tomador" component={PortalTomadorPage} />
          <Route path="/tomador" component={PortalTomadorPage} />
          <Route path="/consulta-nota" component={PortalTomadorPage} />
          <Route path="/portal/*" component={PortalRoutes} />
          <Route path="/portal" component={PortalRoutes} />
          <Route path="/admin/*" component={AdminRoutes} />
          <Route path="/admin" component={AdminRoutes} />
          <Route path="/404" component={NotFound} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    );
  }

  if (isBriqueziNet) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/" component={BriqueziLandingPage} />
          <Route path="/login" component={Login} />
          <Route path="/recuperar-senha" component={RecuperarSenhaPage} />
          <Route path="/esqueci-senha" component={RecuperarSenhaPage} />
          <Route path="/reset-password" component={RecuperarSenhaPage} />
          <Route path="/termos-de-uso" component={TermosDeUsoPage} />
          <Route path="/termos" component={TermosDeUsoPage} />
          <Route path="/politica-de-privacidade" component={PoliticaPrivacidadePage} />
          <Route path="/privacidade" component={PoliticaPrivacidadePage} />
          <Route path="/portal-tomador" component={PortalTomadorPage} />
          <Route path="/tomador" component={PortalTomadorPage} />
          <Route path="/consulta-nota" component={PortalTomadorPage} />
          <Route path="/portal/*" component={PortalRoutes} />
          <Route path="/portal" component={PortalRoutes} />
          <Route path="/admin/*" component={AdminRoutes} />
          <Route path="/admin" component={AdminRoutes} />
          <Route path="/404" component={NotFound} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/">
          <TenantRoute component={Home} />
        </Route>
        <Route path="/login" component={Login} />
        <Route path="/recuperar-senha" component={RecuperarSenhaPage} />
        <Route path="/esqueci-senha" component={RecuperarSenhaPage} />
        <Route path="/reset-password" component={RecuperarSenhaPage} />
        <Route path="/termos-de-uso" component={TermosDeUsoPage} />
        <Route path="/termos" component={TermosDeUsoPage} />
        <Route path="/politica-de-privacidade" component={PoliticaPrivacidadePage} />
        <Route path="/privacidade" component={PoliticaPrivacidadePage} />
          <Route path="/portal-tomador" component={PortalTomadorPage} />
          <Route path="/tomador" component={PortalTomadorPage} />
          <Route path="/consulta-nota" component={PortalTomadorPage} />
        <Route path="/quem-somos">
          <TenantRoute component={QuemSomos} />
        </Route>
        <Route path="/o-que-oferecemos">
          <TenantRoute component={OQueOferecemos} />
        </Route>
        <Route path="/o-que-oferecemos/:slug">
          <TenantRoute component={ServicoDetalhe} />
        </Route>
        <Route path="/contato">
          <TenantRoute component={Contato} />
        </Route>
        <Route path="/blog">
          <TenantRoute component={Blog} />
        </Route>
        <Route path="/blog/:slug">
          <TenantRoute component={BlogPost} />
        </Route>
        <Route path="/portal/*" component={PortalRoutes} />
        <Route path="/portal" component={PortalRoutes} />
        <Route path="/admin/*" component={AdminRoutes} />
        <Route path="/admin" component={AdminRoutes} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <InactivityManager />
          <ScrollToTop />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
